/**
 * product-gallery.js
 *
 * The gallery is CSS: a stack on desktop, a scroll-snap carousel on mobile.
 * This only keeps the position indicator honest and moves the carousel when a
 * variant selects a different image.
 */

class ProductGallery {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-media-track]');
    this.dots = Array.from(root.querySelectorAll('[data-media-dot]'));
    this.status = root.querySelector('[data-media-status]');
    this.items = Array.from(root.querySelectorAll('[data-media-id]'));

    if (!this.track) return;

    if (this.dots.length > 0 && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
        root: this.track,
        threshold: 0.6,
      });
      this.items.forEach((item) => this.observer.observe(item));
    }

    document.addEventListener('theme:variant:media', (event) => this.showMedia(event.detail.mediaId));
  }

  onIntersect(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      this.setCurrent(parseInt(entry.target.dataset.mediaIndex, 10));
    });
  }

  setCurrent(index) {
    this.dots.forEach((dot, i) => {
      if (i === index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });

    if (this.status) {
      this.status.textContent = `${index + 1} / ${this.items.length}`;
    }
  }

  showMedia(mediaId) {
    const target = this.items.find((item) => item.dataset.mediaId === String(mediaId));
    if (!target) return;

    // Desktop stacks the media, so scrolling the page to it would yank the
    // visitor away from the purchase panel. Only the mobile carousel moves.
    const isCarousel = this.track.scrollWidth > this.track.clientWidth + 4;
    if (!isCarousel) return;

    this.track.scrollTo({
      left: target.offsetLeft - this.track.offsetLeft,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }
}

document.querySelectorAll('[data-product-gallery]').forEach((root) => new ProductGallery(root));

export default ProductGallery;
